import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { NamedIcon } from '@shared/ui/icons';
import TopReelsWeek from './TopReelsWeek';
import CreatorBrandHistory from './CreatorBrandHistory';
import { CreatorAdLoad, CreatorTopPosts } from './CreatorAdLoad';
import CreatorGrow from './CreatorGrow';
import type { Digest } from './CreatorHome';

// Detail pages reached from «Дом». They used to be one long feed under the digest;
// each is now its own address, so the phone's Back button returns to «Дом».

function SubPage({ title, wide, children }: { title: string; wide?: boolean; children: ReactNode }) {
  const { t } = useTranslation('provider');
  const navigate = useNavigate();
  // Opened from «Дом» → step back; opened from a link → go to «Дом».
  const back = () => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/provider'));
  return (
    <div className={wide ? 'cr-page wide' : 'cr-page'}>
      <button type="button" className="cr-back" onClick={back}>
        <span aria-hidden="true">‹</span> {t('creator.nav.home', { defaultValue: 'Дом' })}
      </button>
      <h1 className="cr-h2">{title}</h1>
      {children}
    </div>
  );
}

export function CreatorStatsPage() {
  const { t } = useTranslation('provider');
  const { hash } = useLocation();
  const q = useSupabaseQuery<Digest | null>(['creator-digest'], async () => supabase.rpc('creator_home_digest', {}));
  const d = q.data;

  // «Среди похожих» on «Дом» links here with #creator-peers.
  useEffect(() => {
    if (!hash || q.isLoading) return;
    const id = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }), 300);
    return () => clearTimeout(id);
  }, [hash, q.isLoading]);

  return (
    <SubPage title={t('creator.stats.title', { defaultValue: 'Ваша статистика' })} wide>
      {q.isLoading ? <div style={{ color: 'var(--dim)', padding: 30, textAlign: 'center' }}>…</div> : (
        <div className="cr-stats">
          <div>
            {d && (
              <>
                <CreatorAdLoad adLoad={d.ad_load ?? null} peers={d.ad_load_peers ?? null}
                  storyAdLoad={d.story_ad_load ?? null} postsCount={d.posts_90d ?? null} />
                <CreatorTopPosts posts={d.top_posts ?? []} />
              </>
            )}
          </div>
          <CreatorGrow />
        </div>
      )}
    </SubPage>
  );
}

export function CreatorBrandsPage() {
  const { t } = useTranslation('provider');
  return (
    <SubPage title={t('creator.brands.title', { defaultValue: 'С кем вы уже работали' })}>
      <p style={{ color: 'var(--muted)', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 }}>
        {t('creator.brands.pageSub', { defaultValue: 'Бренды, которые вы отмечали в постах и сторис. Нажмите на бренд, чтобы увидеть посты, написать ему или отправить о себе.' })}
      </p>
      <CreatorBrandHistory all bare />
      <BrandsEmpty />
    </SubPage>
  );
}

// CreatorBrandHistory renders nothing without brands — say so instead of a blank page.
function BrandsEmpty() {
  const { t } = useTranslation('provider');
  const { data, isLoading } = useSupabaseQuery<{ ok?: boolean; brands?: unknown[] }>(['my-brand-history'], async () => supabase.rpc('get_my_brand_history'));
  if (isLoading || (data?.brands?.length ?? 0) > 0) return null;
  return (
    <div style={{ color: 'var(--dim)', fontSize: 13, padding: '20px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
      <NamedIcon name="workers" size={16} color="#667" />
      {t('creator.brands.empty', { defaultValue: 'Пока не нашли брендов в ваших постах. Отмечайте бренды через @ — они появятся здесь.' })}
    </div>
  );
}

export function CreatorTopReelsPage() {
  const { t } = useTranslation('provider');
  return (
    <SubPage title={t('creator.home.topReels', { defaultValue: 'Топ рилс недели в Кыргызстане' })} wide>
      <TopReelsWeek limit={12} bare />
    </SubPage>
  );
}
